import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Card,
  Container,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Error403 from "../Error/403";
import GenericQuizScore from "./GenericQuizScore";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import BarChartIcon from "@mui/icons-material/BarChart";
import SchoolIcon from "@mui/icons-material/School";

// Lab type mapping configuration
const LAB_TYPE_MAP = {
  left: { key: 'preconfig', label: 'Preconfigured', view: 'left' },
  center: { key: 'custom', label: 'Custom', view: 'center' },
  right: { key: 'hvm', label: 'Hypervised', view: 'right' },
  justify: { key: 'svm', label: 'Shared', view: 'justify' },
  trainer: { key: 'cloud', label: 'Cloud', view: 'trainer' },
  trainer1: { key: 'wise', label: 'Wise', view: 'trainer1' },
  trainer2: { key: 'manual', label: 'Manual', view: 'trainer2' },
  trainer3: { key: 'saas', label: 'SaaS', view: 'trainer3' },
};

const QuizScore = ({ ...rest }) => {
  const [alignment, setAlignment] = useState("left");
  const [searchTerm, setSearchTerm] = useState("");
  const [assignmentSearchTerm, setAssignmentSearchTerm] = useState("");
  const [currentStep, setCurrentStep] = useState(1);

  // Get current lab type configuration
  const currentLabConfig = useMemo(() => {
    return LAB_TYPE_MAP[alignment] || LAB_TYPE_MAP.left;
  }, [alignment]);

  const handleLabTypeChange = useCallback((event) => {
    const alignmentType = event.target.value;
    setAlignment(alignmentType);
    // Reset search and step when lab type changes
    setSearchTerm("");
    setAssignmentSearchTerm("");
    setCurrentStep(1);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleAssignmentSearchChange = useCallback((e) => {
    setAssignmentSearchTerm(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
  }, []);

  const handleClearAssignmentSearch = useCallback(() => {
    setAssignmentSearchTerm("");
  }, []);

  // Lab type configurations
  const labConfigs = useMemo(() => ({
    preconfig: {
      labType: "preconfig",
      apiEndpoint: "/api/editquizview/preconfig",
      traineeCountEndpoint: "/api/pclabquizscore/",
      fieldMapping: {
        labIdField: "lab_id",
        labNameField: "lab_Name",
        startDateField: "start_date",
        endDateField: "end_date",
      },
    },
    custom: {
      labType: "custom",
      apiEndpoint: "/api/editquizview/custom",
      traineeCountEndpoint: "/api/customlabquizscore/",
      fieldMapping: {
        labIdField: "lab_id",
        labNameField: "lab_Name",
        startDateField: "start_date",
        endDateField: "end_date",
      },
    },
    hvm: {
      labType: "hvm",
      apiEndpoint: "/api/editquizview/hvm",
      traineeCountEndpoint: "/api/hvmlabquizscore/",
      fieldMapping: {
        labIdField: "lab_id",
        labNameField: "lab_Name",
        startDateField: "start_date",
        endDateField: "end_date",
      },
    },
    svm: {
      labType: "svm",
      apiEndpoint: "/api/editquizview/svm",
      traineeCountEndpoint: "/api/svmlabquizscore/",
      fieldMapping: {
        labIdField: "lab_id",
        labNameField: "lab_Name",
        startDateField: "start_date",
        endDateField: "end_date",
      },
    },
    cloud: {
      labType: "cloud",
      apiEndpoint: "/api/editquizview/cloud",
      traineeCountEndpoint: "/api/cloudlabquizscore/",
      fieldMapping: {
        labIdField: "id",
        labNameField: "lab_name",
        startDateField: "start_period",
        endDateField: "end_period",
      },
    },
    wise: {
      labType: "wise",
      apiEndpoint: "/api/editquizview/wise",
      traineeCountEndpoint: "/api/wiselabquizscore/",
      fieldMapping: {
        labIdField: "lab_id",
        labNameField: "lab_Name",
        startDateField: "start_date",
        endDateField: "end_date",
      },
    },
    manual: {
      labType: "manual",
      apiEndpoint: "/api/editquizview/manual",
      traineeCountEndpoint: "/api/manuallabquizscore/",
      fieldMapping: {
        labIdField: "lab_id",
        labNameField: "lab_Name",
        startDateField: "start_date",
        endDateField: "end_date",
      },
    },
    saas: {
      labType: "saas",
      apiEndpoint: "/api/editquizview/saas",
      traineeCountEndpoint: "/api/saslabquizscore/",
      fieldMapping: {
        labIdField: "lab_id",
        labNameField: "lab_Name",
        startDateField: "start_date",
        endDateField: "end_date",
      },
    },
  }), []);

  const getCurrentConfig = useCallback(() => {
    return labConfigs[currentLabConfig.key] || labConfigs.preconfig;
  }, [currentLabConfig.key, labConfigs]);

  // Check permissions
  const hasPermission = !(
    localStorage.getItem("user_access") === "You do not have permission to perform this action." &&
    localStorage.getItem("group") !== "trainer" && 
    localStorage.getItem("group") !== "admin"
  );

  if (!hasPermission) {
    return <Error403 />;
  }

  return (
    <>
      <Helmet>
        <title>LabAsService - View Assessment Scores</title>
      </Helmet>
      <Container maxWidth={false} sx={{ py: 6, pb: 8, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
        <Card {...rest} sx={{ mb: 4 }}>
          {/* Hero Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(46,125,50,0.12), rgba(21,101,192,0.12))',
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              p: { xs: 2.5, md: 3 },
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <BarChartIcon color="primary" sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="h4" component="h1">
                  View Assessment Scores
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View and analyze assessment scores for {currentLabConfig.label.toLowerCase()} labs
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Controls Section */}
          <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              sx={{ mb: 3 }}
            >
              {/* Lab Type Selector */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: { sm: 280 } }}>
                <SchoolIcon color="primary" />
                <Typography
                  sx={{
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                    fontSize: '1rem',
                    color: 'text.primary',
                  }}
                >
                  Lab Type
              </Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  labelId="labType-label"
                  id="labType"
                    value={alignment}
                    onChange={handleLabTypeChange}
                    sx={{
                      backgroundColor: 'white',
                      '& .MuiSelect-select': {
                        py: 1,
                      },
                    }}
                  >
                    {Object.entries(LAB_TYPE_MAP).map(([value, config]) => (
                      <MenuItem key={value} value={value}>
                        {config.label}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>

              {/* Search Fields */}
              {alignment && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', flex: 1, maxWidth: { xs: '100%', sm: 400 } }}>
                  {currentStep === 1 && (
                    <TextField
                      placeholder="Search Lab Name"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      size="small"
                      sx={{ width: '100%' }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={handleClearSearch}
                              aria-label="clear search"
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  {currentStep === 2 && (
                    <TextField
                      placeholder="Search Assignment Name"
                      value={assignmentSearchTerm}
                      onChange={handleAssignmentSearchChange}
                      size="small"
                      sx={{ width: '100%' }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: assignmentSearchTerm && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={handleClearAssignmentSearch}
                              aria-label="clear assignment search"
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
              </Box>
              )}
            </Stack>

            {/* GenericQuizScore Component */}
            {alignment && (
              <Box sx={{ pt: 0 }}>
                <GenericQuizScore 
                  config={getCurrentConfig()} 
                  alignment={alignment} 
                  searchTerm={searchTerm} 
                  setSearchTerm={setSearchTerm}
                  assignmentSearchTerm={assignmentSearchTerm}
                  setAssignmentSearchTerm={setAssignmentSearchTerm}
                  onStepChange={setCurrentStep}
                />
              </Box>
            )}
          </Box>
          </Card>
      </Container>
    </>
  );
};

export default QuizScore;
